import { Car, ChevronRight } from 'lucide-preact';
import { route } from 'preact-router';
import { StatusBadge } from '../../components/ui/Badge.js';
import { BackToFirstPage, Pager } from '../../components/ui/Pager.js';
import { ResultState } from '../../components/ui/ResultState.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { toRequestedPage } from '../../components/ui/pageParam.js';
import { goneMessage, pageGoneMessage } from '../../components/ui/resultWording.js';
import { useFetch } from '../../hooks/useFetch.js';
import { useSignInRedirect } from '../../hooks/useSignInRedirect.js';

/** 052: resolved vehicle summary. Null when the car record is gone (FR-005). */
interface BookingCar {
  id: string;
  /** Composed brand + model — never the `·` specification dump. */
  label: string;
  plate_number: string;
  image_url: string | null;
}

interface BookingLocation {
  id: string;
  name: string;
  city_name: string | null;
}

interface Booking {
  /** 053: canonical identifier. `_id` is still on the wire but deprecated. */
  id: string;
  car_id: string;
  pickup_at: string;
  return_at: string;
  status: string;
  total_price_minor: number;
  currency: string;
  car: BookingCar | null;
  location: BookingLocation | null;
  /**
   * 325 (#477): the rental this booking heads: an original booking and its extensions, listed as ONE
   * card. Absent only from a platform older than 325, when the card falls back to the booking itself.
   */
  rental?: RentalSummary;
}

/** `summariseRental`'s shape (`backend/src/modules/bookings/rental.ts`), dates as sent. */
interface RentalSummary {
  pickup_at: string;
  return_at: string;
  total_price_minor: number;
  extension_count: number;
  status: string;
}

/**
 * 066: the envelope `GET /bookings` has always sent. The screen used to declare only `items` and
 * read only `items`, which is why a customer with more than one page of history was shown a
 * truncated list presented as a complete one (gap-list D5).
 *
 * Transcribed from `backend/src/modules/bookings/routes.ts:211` —
 * `specs/066-app-bookings-pagination/contracts/bookings-list.md`.
 */
interface BookingsResponse {
  /** One page of bookings. **Empty does NOT mean the customer has none** — see `total`. */
  items: Booking[];
  /** Bookings matching across ALL pages. */
  total: number;
  /** Echoed from the request, clamped only to >= 1 — never clamped down to `pages`. */
  page: number;
  /** `Math.ceil(total / limit)`, so **0** for an empty history. */
  pages: number;
}

interface BookingsPageProps {
  path?: string;
  /**
   * 066: the page being asked for, arriving as a **string** from the address —
   * `/account/bookings?page=3`. preact-router parses the query string into route props and
   * `AuthGuard` forwards them to this screen (research R2).
   *
   * The address is the state: it survives opening a booking and coming back (FR-014), it can be
   * shared and bookmarked, and because the page number ends up in the fetch path, a retry
   * re-requests the page that failed rather than page 1 (FR-009).
   */
  page?: string;
  /**
   * 152 (#15): the stage being looked at, arriving as a string from the address the same way
   * `page` does — `/account/bookings?tab=history`. Anything unrecognised reads as Active rather
   * than erroring, the posture `toRequestedPage` already takes for a malformed page number.
   */
  tab?: string;
}

/**
 * 152: the three stages, as the customer's words for them.
 *
 * The screen names the stages and lets the platform decide MEMBERSHIP — which statuses each one
 * holds lives once, in `backend/src/modules/bookings/stages.ts`. A list of statuses here would be
 * a second definition, free to drift from the one the query uses.
 */
const TABS = [
  { stage: 'active', label: 'Active' },
  { stage: 'upcoming', label: 'Upcoming' },
  { stage: 'history', label: 'History' },
] as const;

type Stage = (typeof TABS)[number]['stage'];

function toStage(raw: string | undefined): Stage {
  const normalized = raw?.trim().toLowerCase();
  return (TABS.find((t) => t.stage === normalized)?.stage ?? 'active') as Stage;
}

/**
 * Each stage's own way of being empty. Three sentences rather than one, because "no rental in
 * progress" and "no past rentals" are different facts and a customer reading the wrong one would
 * think their history had been lost.
 *
 * Kept here rather than in `resultWording.ts`: that module carries the wording for a read that
 * FAILED or found nothing where something was expected, and a screen supplies only the noun. An
 * empty stage is a successful read with a true answer, and the existing "No bookings yet" copy
 * this replaces already lived here.
 */
const EMPTY_STAGE_MESSAGE: Record<Stage, string> = {
  // 192 (#28): Active is the tab a customer lands on, so its empty state is the first thing many of
  // them read. It used to stop at "No rental in progress", which told someone who HAD bookings
  // nothing about where they were — the reporter of #28 concluded their bookings had not saved.
  //
  // 🔴 Phrased as where bookings LIVE, never as a claim that this customer has any. "You have 2
  // upcoming bookings" would need a second query, and would be a lie for the customer who has none.
  active: "No rental in progress right now. Anything you've booked ahead is under Upcoming.",
  upcoming: 'Nothing booked yet.',
  history: 'No past rentals yet.',
};

/**
 * 052: the thumbnail tile. `image_url` is null for every car without images, which is
 * the common case in current data — so the placeholder is the normal path, and a broken
 * <img> must never be rendered (FR-003).
 */
function Thumbnail({ car }: { car: BookingCar | null }) {
  if (car?.image_url) {
    return (
      <img
        src={car.image_url}
        alt={car.label}
        className="w-16 h-12 rounded-lg object-cover shrink-0"
      />
    );
  }
  return (
    <div
      data-testid="booking-car-thumbnail-fallback"
      className="w-16 h-12 rounded-lg bg-surface-mid shrink-0 flex items-center justify-center"
    >
      <Car size={20} className="text-gray-500" aria-hidden="true" />
    </div>
  );
}

/**
 * #541: a rental's dates and times are shown in Malaysia wall-clock time, never the device's zone.
 * The car is collected at a Malaysian location and the booking form's slots are MYT
 * (`useDateTimePicker.ts`), so a customer whose device is set elsewhere must still read the time
 * they booked. The date uses the same zone as the time; otherwise a 01:30 pickup could sit under
 * the previous day's date.
 */
const RENTAL_ZONE = 'Asia/Kuala_Lumpur';

function rentalDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { timeZone: RENTAL_ZONE });
}

function rentalTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    timeZone: RENTAL_ZONE,
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * 066: moving pages is a navigation, not a state change.
 *
 * Page 1 is the plain address, so the customer's normal entry point never grows a redundant
 * `?page=1` and the two spellings of "the first page" do not both end up in their history.
 */
function bookingsAddress(stage: Stage, n: number): string {
  const params = new URLSearchParams();
  // Active and page 1 are the defaults, so neither appears — the customer's normal entry point
  // stays the plain address and the two spellings of it never both reach their history.
  if (stage !== 'active') params.set('tab', stage);
  if (n > 1) params.set('page', String(n));
  const query = params.toString();
  return query ? `/account/bookings?${query}` : '/account/bookings';
}

function goToPage(stage: Stage, n: number): void {
  route(bookingsAddress(stage, n));
}

export function BookingsPage({ page, tab }: BookingsPageProps) {
  const goToSignIn = useSignInRedirect();
  const requestedPage = toRequestedPage(page);
  const stage = toStage(tab);
  const bookingsRead = useFetch<BookingsResponse>(`/bookings?stage=${stage}&page=${requestedPage}`);
  const { data, status } = bookingsRead;
  const loading = status === 'loading' || status === 'idle';

  return (
    // 137: bounded to exactly the space the shell leaves available (100dvh minus its 132px
    // chrome, unchanged since 134/136) and clipped, not merely floored — the outer page can
    // never grow past this, so it never asks the document for more height than the viewport
    // already has, so the shell's notification bell (above this page, outside it) is never
    // displaced by scrolling. Only the region below the header scrolls.
    <div className="page-bound bg-bg px-4 pt-6 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-text">My Bookings</h1>
        <button
          type="button"
          onClick={() => route('/account/reviews')}
          className="text-sm text-primary font-semibold"
        >
          My Reviews
          <ChevronRight size={16} aria-hidden="true" className="inline" />
        </button>
      </div>
      {/* 152: the tabs sit in the FIXED part of the page, above the scrolling region 137
        established, so they stay reachable while a long history scrolls under them — and the
        page still never grows past the viewport, so the shell's bell is never displaced. */}
      <div
        role="tablist"
        aria-label="Booking stages"
        className="flex gap-1 border-b border-hairline mb-4"
      >
        {TABS.map((t) => {
          const selected = t.stage === stage;
          return (
            <button
              key={t.stage}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => route(bookingsAddress(t.stage, 1))}
              className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${
                selected ? 'border-primary text-primary' : 'border-transparent text-gray-400'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <div
        data-testid="page-scroll-region"
        className="flex-1 overflow-y-auto overscroll-y-contain pb-6"
      >
        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : status !== 'ok' ? (
          /* 064: an ended session used to read as "No bookings yet" — the customer believed their
           records were gone. Reaching the empty copy below now means the platform answered. */
          <>
            <ResultState
              state={bookingsRead}
              onRetry={bookingsRead.retry}
              onSignIn={goToSignIn}
              goneMessage={goneMessage('bookings', true)}
              onwardLabel="Back to search"
              onOnward={() => route('/search')}
            />
            {/* 066 FR-011: a read that failed on page 3 leaves the customer with a retry and a way
              out of the app, but nothing leading back to the bookings they were just reading.
              Offered only past the first page, where there is somewhere to go back to. */}
            {requestedPage > 1 && (
              <div className="flex justify-center">
                <BackToFirstPage onFirstPage={() => goToPage(stage, 1)} />
              </div>
            )}
          </>
        ) : data && !data.items.length && data.total > 0 ? (
          /* 066 FR-008: an EMPTY SLICE of a non-empty history — `page` is echoed by the platform,
           never clamped down to `pages`, so a stale bookmark or a shrunken history lands here with
           45 bookings and nothing on this page. Falling through to the copy below would tell a
           customer with a full history that they have none: 064's defect, one page over. */
          <div className="flex flex-col items-center gap-3 py-16">
            <p className="text-center text-error">{pageGoneMessage('bookings')}</p>
            <BackToFirstPage onFirstPage={() => goToPage(stage, 1)} />
          </div>
        ) : !data?.items.length ? (
          /* 152: each stage's own way of being empty. Reaching this means the platform
           answered and this stage genuinely holds nothing — the failed, refused and
           empty-slice branches above all state themselves first. */
          <p className="text-center text-gray-400 py-16">{EMPTY_STAGE_MESSAGE[stage]}</p>
        ) : (
          <div className="flex flex-col gap-3">
            {data.items.map((b) => {
              // 325: the card reads as the RENTAL (status, total, final return), so an extended
              // rental is one card, and an extension paid before it starts never reads "in use".
              const view = b.rental ?? b;
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => route(`/booking/${b.id}/confirm`)}
                  className="bg-surface-card rounded-xl p-4 text-left flex flex-col gap-2 w-full"
                >
                  <div className="flex gap-3">
                    <Thumbnail car={b.car} />
                    <div className="flex-1 min-w-0">
                      {b.car ? (
                        <>
                          <p className="font-semibold text-text truncate">{b.car.label}</p>
                          <p className="text-xs text-gray-500">{b.car.plate_number}</p>
                        </>
                      ) : (
                        // The car record is gone, but the booking is not: the customer still
                        // needs its price, its status and its cancel control.
                        <p className="font-semibold text-gray-400 truncate">
                          Vehicle no longer listed
                        </p>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="whitespace-nowrap">
                          <StatusBadge status={view.status} />
                        </span>
                        {b.rental && b.rental.extension_count > 0 && (
                          <span
                            data-testid="booking-extended"
                            className="whitespace-nowrap text-xs text-gray-400"
                          >
                            Extended ×{b.rental.extension_count}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-primary font-semibold shrink-0">
                      RM {(view.total_price_minor / 100).toFixed(2)}
                    </span>
                  </div>
                  {/* 401 (#401): the LOCATION NAME ("UMSKAL"), not the city ("Victoria") — the
                    data was already on the wire (`bookings/hydrate.ts`); this line simply read
                    the wrong field, unlike BookingConfirmPage.tsx which already reads `.name`. */}
                  {b.location?.name && (
                    <div className="text-sm text-gray-400">{b.location.name}</div>
                  )}
                  {/* #541: the time sits under the date, because a same-day rental's dates alone say
                    nothing. Both are Malaysia time (see RENTAL_ZONE), so they never disagree. */}
                  <div>
                    <div data-testid="booking-dates" className="text-sm text-gray-400">
                      {rentalDate(view.pickup_at)} → {rentalDate(view.return_at)}
                    </div>
                    <div data-testid="booking-times" className="text-sm font-semibold text-text">
                      {rentalTime(view.pickup_at)} → {rentalTime(view.return_at)}
                    </div>
                  </div>
                </button>
              );
            })}
            {/* Rendered only on a successful read, where `pages` is something the platform just
              said. A pager painted from a remembered count beside a failed page would be the app
              asserting what it was not told — the defect class this feature belongs to. */}
            <Pager page={data.page} pages={data.pages} onPage={(n) => goToPage(stage, n)} />
          </div>
        )}
      </div>
    </div>
  );
}
